import { Computer, getComputerPrinters, getComputers, Printer } from "@api/PrintNode";
import CSReactSelect, { CSOption } from "@components/Form/CSReactSelect";
import SectionSubGroup from "@components/SectionGroup/SectionSubGroup";
import { UseState } from "@interfaces/State";
import Localization from "@localization/Index";
import { ConfigurationContext } from "@providers/ConfigurationProvider";
import { showError } from "@utils/NotificationsUtils";
import { getUserWarehouses } from "@utils/WarehousesUtils";
import { Warehouse } from "cs.node.utils/csapi/provisioning";
import { createContext, CSSProperties, useContext, useEffect, useState } from "react";
import { Accordion, useAccordionButton } from "react-bootstrap";
import { GroupBase, OptionProps, SingleValueProps } from "react-select";
import PrintNodeManager from "./PrintNodeManager";

type PackagingConfiguratorContextType = {
    configuratorOpenState: UseState<boolean>
    warehouses?: Warehouse[]
    printNodeComputerList?: Computer[]
    printNodePrinterListState: UseState<Printer[] | undefined>
    selectedWhId?: string
    selectedPrintNodeComputerId?: string
    selectedZebraPrinterId?: string
    selectedStandardPrinterId?: string
    selectedServicePrinterId?: string
}

const PackagingConfiguratorContext = createContext<PackagingConfiguratorContextType>(null!)

const CSStateOption = (props: OptionProps<unknown>) => {
    const {
        data,
        children,
        className,
        cx,
        getStyles,
        isDisabled,
        isFocused,
        isSelected,
        innerRef,
        innerProps,
    } = props

    const { additional_info } = data as CSOption
    const online = additional_info?.online as boolean | undefined

    return (
        <div
            ref={innerRef}
            style={getStyles('option', props) as CSSProperties}
            className={`d-flex justify-content-start align-items-start ${cx(
            {
                option: true,
                'option--is-disabled': isDisabled,
                'option--is-focused': isFocused,
                'option--is-selected': isSelected,
            },
            className
            )}`}
            {...innerProps}>
            <i className={`bi-circle-fill me-2 ${online ? 'text-success' : 'text-danger'}`}></i>
            <span>{children}</span>
        </div>
    )
}

const CSStateSingleValue = (props: SingleValueProps<unknown, boolean, GroupBase<unknown>>) =>{
    const {
        data,
        children,
        className,
        cx,
        getStyles,
        isDisabled,
        innerProps,
    } = props

    const { additional_info } = data as CSOption
    const online = additional_info?.online as boolean | undefined

    return (
        <div
            style={getStyles('singleValue', props) as CSSProperties}
            className={`d-flex justify-content-start align-items-center ${cx(
            {
                option: true,
                'option--is-disabled': isDisabled
            },
            className
            )}`}
            {...innerProps}>
            <i className={`bi-circle-fill me-2 ${online ? 'text-success' : 'text-danger'}`}></i>
            <span className="ms-2">{children}</span>
        </div>
    )
}

const PackagingConfigurator = ({children}:{children: React.ReactNode})=>{
    const configurationContext = useContext(ConfigurationContext)

    const selectedWhId = configurationContext.getValue('packaging_wh_id', undefined) as string|undefined
    const selectedPrintNodeComputerId = configurationContext.getValue('packaging_computer_id', undefined) as string|undefined
    const selectedZebraPrinterId = configurationContext.getValue('packaging_zebra_printer_id', undefined) as string|undefined
    const selectedStandardPrinterId = configurationContext.getValue('packaging_standard_printer_id', undefined) as string|undefined
    const selectedServicePrinterId = configurationContext.getValue('packaging_service_printer_id', undefined) as string|undefined
    const [configuratorOpen, setConfiguratorOpen] = useState(!selectedWhId)
    const [warehouses, setWarehouses] = useState<Warehouse[]>()
    const [printNodeComputerList, setPrintNodeComputerList] = useState<Computer[]>()
    const [printNodePrinterList, setPrinterNodePrinterList] = useState<Printer[]>()

    const providerValue: PackagingConfiguratorContextType = {
        configuratorOpenState: [configuratorOpen, setConfiguratorOpen],
        warehouses,
        printNodeComputerList,
        printNodePrinterListState: [printNodePrinterList, setPrinterNodePrinterList],
        selectedWhId,
        selectedPrintNodeComputerId,
        selectedZebraPrinterId,
        selectedStandardPrinterId,
        selectedServicePrinterId
    }

    const loadComputerPrinters = async()=>{
        
        setPrinterNodePrinterList(undefined)
        if(selectedPrintNodeComputerId) {
            try{
                const printers = await getComputerPrinters(selectedPrintNodeComputerId)
                setPrinterNodePrinterList(printers)
            } catch(err) {
                showError(Localization.ERRORE_GENERICO)
            }
        }
    }

    const init = async()=>{
        setWarehouses(await getUserWarehouses())
        setPrintNodeComputerList(await getComputers())
    }

    useEffect(()=>{
        if(printNodePrinterList) {
            if(!printNodePrinterList.find(printer=>printer.id === selectedZebraPrinterId)){
                configurationContext.setValue("packaging_zebra_printer_id", undefined)   
            }
            if(!printNodePrinterList.find(printer=>printer.id === selectedStandardPrinterId)){
                configurationContext.setValue("packaging_standard_printer_id", undefined)
            }
            if(!printNodePrinterList.find(printer=>printer.id === selectedServicePrinterId)){
                configurationContext.setValue("packaging_service_printer_id", undefined)
            }
        }
    }, [printNodePrinterList])

    useEffect(()=>{
        init()
    }, [])

    useEffect(()=>{
        loadComputerPrinters()
    }, [selectedPrintNodeComputerId])

    return (
        <PackagingConfiguratorContext.Provider value={providerValue}>
            <Accordion defaultActiveKey={configuratorOpen?"packaging-configurator":undefined} >
            {children}
            </Accordion>
        </PackagingConfiguratorContext.Provider>
    )
}

const Toolbar = ()=>{
    const context = useContext(PackagingConfiguratorContext)

    if(context === null) {
        return null
    }

    const { configuratorOpenState } = context
    const [configuratorOpen, setConfiguratorOpen] = configuratorOpenState

    const AccordionButton = ({ eventKey }: {eventKey: string}) => {
        const decoratedOnClick = useAccordionButton(eventKey, () =>
            setConfiguratorOpen(prev => !prev)
        )

        return <i role="button" onClick={decoratedOnClick} className={`bi-sliders text-${configuratorOpen?"primary":"muted"} font-2x ms-3`}></i>
    }

    return <>
        <AccordionButton eventKey="packaging-configurator" />
        <PrintNodeManager />
    </>
}

const Content = ()=>{
    const context = useContext(PackagingConfiguratorContext)
    const configurationContext = useContext(ConfigurationContext)

    if(context === null) {
        return null
    }

    const { warehouses, printNodeComputerList, printNodePrinterListState, selectedPrintNodeComputerId, selectedZebraPrinterId, selectedStandardPrinterId, selectedServicePrinterId } = context
    const [printNodePrinterList] = printNodePrinterListState
    const selectedWhId = configurationContext.getValue('packaging_wh_id', undefined) as string|undefined

    const warehouseOptions:CSOption[] = (warehouses ?? []).map(warehouse=>{
        return {
            value: warehouse._id?.$oid!,
            label: warehouse.name!
        }
    })

    const computerOptions:CSOption[] = (printNodeComputerList ?? []).map(computer=>{
        return {
            value: computer.id,
            label: computer.name,
            additional_info: { online: computer.state === "connected" }
        }
    })

    const printerOptions:CSOption[] = (printNodePrinterList ?? []).map(printer=>{
        return {
            value: printer.id,
            label: printer.name,
            additional_info: { online: printer.state === "online" }
        }
    })

    const warehouseSelected = warehouseOptions.find(warehouseOption=>warehouseOption.value === selectedWhId)
    const computerSelected = computerOptions?.find(computer=>computer.value === selectedPrintNodeComputerId)
    const zebraPrinterOptionSelected = printerOptions?.find(printer=>printer.value === selectedZebraPrinterId)
    const standardPrinterOptionSelected = printerOptions?.find(printer=>printer.value === selectedStandardPrinterId)
    const servicePrinterOptionSelected = printerOptions?.find(printer=>printer.value === selectedServicePrinterId)

    return <Accordion.Collapse eventKey="packaging-configurator">
        <SectionSubGroup>
            <div className="row mt-field">
                <div className="col-md-12">
                    <CSReactSelect 
                        client 
                        className="me-3"
                        controlId="warehouse_select"
                        placeholder={Localization.PAGE_PACKAGING.SELEZIONA_UN_MAGAZZINO}
                        options={warehouseOptions}
                        value={warehouseSelected}
                        onChange={(option:CSOption)=>{
                            configurationContext.setValue("packaging_wh_id", option.value)
                        }}  />
                </div>
                <div className="col-md-12">
                        <CSReactSelect
                            client
                            className="me-3"
                            controlId="computer_select"
                            placeholder={Localization.PAGE_PACKAGING.SELEZIONA_UN_COMPUTER}
                            options={computerOptions}
                            value={computerSelected}
                            components={{Option: CSStateOption, SingleValue: CSStateSingleValue}}
                            styles={{
                                option: (base) => ({...base}),
                                singleValue: (base) => ({...base}),
                            }}
                            onChange={(option:CSOption)=>{
                                configurationContext.setValue("packaging_computer_id", option.value)
                            }}  />
                    </div>
            </div>
            <div className="row mt-field">
                <div className="col-md-8">
                    <div className="d-flex align-items-center">
                        <img className="me-4" alt="zebra_small" src="/zebra/zebra_small.jpeg" style={{height: '2.5rem'}} />
                        <CSReactSelect
                            disabled={selectedPrintNodeComputerId===undefined}
                            placeholder={Localization.PAGE_PACKAGING.SELEZIONA_UNA_STAMPANTE_ZEBRA}
                            client
                            controlId="zebra_select"
                            options={printerOptions}
                            value={zebraPrinterOptionSelected}
                            components={{Option: CSStateOption, SingleValue: CSStateSingleValue}}
                            styles={{
                                option: (base) => ({...base}),
                                singleValue: (base) => ({...base}),
                            }}
                            onChange={(option:CSOption)=>{
                                configurationContext.setValue("packaging_zebra_printer_id", option.value)
                            }}  />
                    </div>
                </div>
                <div className="col-md-8">
                    <div className="d-flex align-items-center">
                        <i className="me-4 bi-printer font-2x text-muted"></i>
                        <CSReactSelect
                            disabled={selectedPrintNodeComputerId===undefined}
                            placeholder={Localization.PAGE_PACKAGING.SELEZIONA_UNA_STAMPANTE_STANDARD}
                            client
                            controlId="standard_select"
                            options={printerOptions}
                            value={standardPrinterOptionSelected}
                            components={{Option: CSStateOption, SingleValue: CSStateSingleValue}}
                            styles={{
                                option: (base) => ({...base}),
                                singleValue: (base) => ({...base}),
                            }}
                            onChange={(option:CSOption)=>{
                                configurationContext.setValue("packaging_standard_printer_id", option.value)
                            }}  />
                    </div>
                </div>
                <div className="col-md-8">
                    <div className="d-flex align-items-center">
                        <i className="me-4 bi-gear font-2x text-muted"></i>
                        <CSReactSelect
                            disabled={selectedPrintNodeComputerId===undefined}
                            placeholder={Localization.PAGE_PACKAGING.SELEZIONA_UNA_STAMPANTE_DI_SERVIZIO}
                            client
                            controlId="service_select"
                            options={printerOptions}
                            value={servicePrinterOptionSelected}
                            components={{Option: CSStateOption, SingleValue: CSStateSingleValue}}
                            styles={{
                                option: (base) => ({...base}),
                                singleValue: (base) => ({...base}),
                            }}
                            onChange={(option:CSOption)=>{
                                configurationContext.setValue("packaging_service_printer_id", option.value)
                            }}  />
                    </div>
                </div>
            </div>
        </SectionSubGroup>
    </Accordion.Collapse>
}

PackagingConfigurator.Toolbar = Toolbar
PackagingConfigurator.Content = Content

export { PackagingConfigurator, PackagingConfiguratorContext }

export default PackagingConfigurator